'use client';

import { automationId } from '@terra/shared-utils';
import { VStack, HStack, Box } from '@terra/ui-styled-system';
import { Cancel01Icon } from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { Drawer } from '../../components/drawer';
import { Button } from '../../components/button';
import {
  BodyDefault,
  BodyLarge,
  HeadingModal,
  MetaDefault,
} from '../../components/typography';
import { Tooltip } from '../../components/tooltip';
import type { ReactNode } from 'react';

export type IconConfig = {
  iconElement: ReactNode;
};
export type HeaderConfig = {
  topic: string;
  appendDetails?: boolean;
  title: ReactNode;
  description?: string;
  wrapTitle?: boolean;
  iconConfig?: IconConfig;
  actions?: ReactNode;
  titleAdornment?: ReactNode;
};
export type CustomDrawerHeaderProps = HeaderConfig & {
  dataTestid?: string;
  density?: 'default' | 'compact';
};
export function CustomDrawerHeader({
  topic,
  appendDetails = true,
  title,
  description,
  wrapTitle,
  iconConfig,
  actions,
  titleAdornment,
  dataTestid = '',
  density = 'default',
}: CustomDrawerHeaderProps) {
  const t = useTranslations('designSystem.customDrawer');

  return (
    <Drawer.Header p={0}>
      <VStack alignItems="start" w="full" gap={0}>
        <HStack
          alignItems="center"
          w="full"
          justifyContent="space-between"
          pl={density === 'compact' ? 6 : 9}
          pt={4}
          pb={3}
          pr={4}
        >
          <MetaDefault color="fg.muted" whiteSpace="nowrap">
            {topic}
            {appendDetails ? ` ${t('details')}` : ''}
          </MetaDefault>
          <HStack gap={2} alignItems="center">
            {actions}
            <Drawer.CloseTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                {...automationId(dataTestid, 'close')}
              >
                <Cancel01Icon size={18} />
              </Button>
            </Drawer.CloseTrigger>
          </HStack>
        </HStack>

        <Box
          pl={density === 'compact' ? 6 : 9}
          pr={density === 'compact' ? 6 : 9}
          pb={4}
          w="full"
        >
          <VStack gap={2} alignItems="flex-start" w="full">
            <HStack
              gap={4}
              alignItems="center"
              justifyContent="flex-start"
              flexWrap="wrap"
              w="full"
              overflow="hidden"
            >
              {iconConfig ? (
                <Box flexShrink={0}>{iconConfig.iconElement}</Box>
              ) : null}

              <Box flex={1} minW={0} overflow="hidden">
                {typeof title === 'string' && !wrapTitle ? (
                  <Tooltip title={title}>
                    <HeadingModal
                      overflow="hidden"
                      textOverflow="ellipsis"
                      whiteSpace="nowrap"
                    >
                      {title}
                    </HeadingModal>
                  </Tooltip>
                ) : (
                  <HeadingModal
                    overflow={wrapTitle ? 'visible' : 'hidden'}
                    textOverflow={wrapTitle ? undefined : 'ellipsis'}
                    whiteSpace={wrapTitle ? 'normal' : 'nowrap'}
                    wordBreak={wrapTitle ? 'break-word' : undefined}
                  >
                    {title}
                  </HeadingModal>
                )}
              </Box>
              {titleAdornment}
            </HStack>
            {description &&
              (density === 'compact' ? (
                <BodyDefault color="fg.default" lineClamp={3}>
                  {description}
                </BodyDefault>
              ) : (
                <BodyLarge color="fg.muted" lineClamp={3}>
                  {description}
                </BodyLarge>
              ))}
          </VStack>
        </Box>
      </VStack>
    </Drawer.Header>
  );
}
